import {DestroyRef, inject, Injectable} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
  filter,
  finalize,
  first,
  forkJoin,
  from,
  map,
  mergeMap,
  Observable,
  of,
  switchMap,
  tap,
  throwError,
  timer,
  toArray
} from 'rxjs';
import {CONSUMPTION_TYPE} from 'src/app/core/enums/consumption-type.enum';
import {Logger} from 'src/app/core/interfaces/Logger';
import {IPaymentDetails, IPaymentSummary} from 'src/app/core/interfaces/payment.interface';
import {HardwareManagerService} from 'src/app/core/services/hardware-manager/hardware-manager.service';
import {PaymentsService} from 'src/app/core/services/payments/payments.service';
import {AccountClosedEvent} from 'src/app/shared/application/events/account/account-closed.event';
import {PAYMENT_METHOD} from 'src/app/shared/enums/payment-method.enum';
import {PAYMENT_TYPE} from 'src/app/shared/enums/payment-type.enum';
import {
  GlobalSplashArtLoaderService
} from 'src/app/shared/services/global-splashart-loader/global-splashart-loader.service';
import {EVENT_BUS} from '../../../../../shared/domain/tokens/event-bus.token';
import {Account} from '../../../account/domain/model/account.model';
import {AccountStorePort} from '../../../account/domain/ports/account-store.port';
import {ACCOUNT_STORE} from '../../../account/domain/tokens/account-store.token';
import {Booking} from '../../../booking/domain/models/booking.model';
import {BookingRepository} from '../../../booking/domain/repositories/booking.repository';
import {BOOKING_REPOSITORY} from '../../../booking/domain/tokens/booking.tokens';
import {ENTRY_TYPE} from '../../../entry/domain/enum/entry-type.enum';
import {Entry} from '../../../entry/domain/model/entry.model';
import {EntryService} from '../../../entry/entry.service';
import {EntryMapper} from '../../../entry/infrastructure/mapper/entry.mapper';
import {IApiPaymentTransactionResponse} from '../../domain/interfaces/payment-api.interface';
import {IPaymentResult} from '../../domain/interfaces/payment-storage.interface';
import {PaymentBookingCompletedEvent} from '../events/payment-booking-completed.event';
import {CASH_AMOUNT_PROMPT} from 'src/app/features/payment/domain/ports/ui/cash-amount-prompt.port';
import {PAYMENT_SETTINGS} from 'src/app/features/payment/domain/ports/ui/payment-settings.port';

@Injectable({providedIn: 'root'})
export class PayBookingAccountUseCase {
  private readonly paymentService = inject(PaymentsService);
  private readonly bookingRepository =
    inject<BookingRepository>(BOOKING_REPOSITORY);
  private readonly accountStoreService =
    inject<AccountStorePort>(ACCOUNT_STORE);
  private readonly entryService = inject(EntryService);
  private readonly logger = inject(Logger);
  private readonly cashPrompt = inject(CASH_AMOUNT_PROMPT);
  private readonly paymentSettings = inject(PAYMENT_SETTINGS);
  private readonly destroyRef = inject(DestroyRef);
  private readonly hardwareManagerService = inject(HardwareManagerService);
  private readonly eventBus = inject(EVENT_BUS);

  private readonly globalSplashArtLoaderService = inject(
    GlobalSplashArtLoaderService
  );

  private selectedBooking: Booking | null = null;
  private selectedAccount: Account | undefined;

  execute(
    paymentMethod: PAYMENT_METHOD
  ): Observable<IPaymentResult> {
    return this.bookingRepository.getActive().pipe(
      first(),
      filter((booking): booking is Booking => Boolean(booking)),
      switchMap((booking) => {
        this.selectedBooking = booking;
        this.selectedAccount = this.getSelectedAccount();

        if (!this.selectedBooking || !this.selectedAccount) {
          throw new Error('No booking or account selected');
        }

        const entriesTotal = this.selectedAccount.entriesTotal;
        const price = this.selectedBooking.financial.bookingPrice ?? 0;
        const cover = this.selectedBooking.financial.coverCharge?.total ?? 0;
        const paid = this.selectedBooking.financial.paid ?? 0;
        const min = Math.max(0, price - cover);
        const excess = Math.max(0, entriesTotal - min);
        const computedToBePaid = Math.max(0, price + excess - paid);
        const totalAmountToPay = Math.round((computedToBePaid + Number.EPSILON) * 100) / 100;

        const entriesToPay$ = this.getEntriesToPay(
          this.selectedAccount,
          this.selectedBooking
        );

        return entriesToPay$.pipe(
          switchMap((entriesToPay) => {
            return this.processPayment(
              paymentMethod,
              totalAmountToPay,
              entriesToPay
            ).pipe(
              switchMap((paymentResult) => {
                if (paymentResult.success) {
                  return timer(600).pipe(
                    switchMap(() => {
                      const loaderRef = this.globalSplashArtLoaderService.open({
                        loadingTexts: ['fv_modal.payment_loader.msg'],
                      });

                      return timer(8000).pipe(
                        tap(() => {
                          this.eventBus.publish(new PaymentBookingCompletedEvent(this.selectedBooking!, paymentResult));
                        }),
                        map(() => paymentResult),
                        finalize(() => {
                          loaderRef.close();
                        })
                      );
                    })
                  );
                }

                return of(paymentResult);
              })
            );
          })
        );
      })
    );
  }

  private getSelectedAccount(): Account | undefined {
    return this.accountStoreService.getSelectedAccount();
  }

  private calculateTotalAmountToPay(booking: Booking): number {
    return booking.financial.toBePaid;
  }

  private getEntriesToPay(
    account: Account,
    booking: Booking
  ): Observable<Entry[]> {
    const existingBookingEntries = (account.entries || [])
      .filter((entry: Entry) => entry.type === ENTRY_TYPE.booking);

    const removeBookingEntries$ = existingBookingEntries.length > 0
      ? from(existingBookingEntries).pipe(
        mergeMap((entry: Entry) => this.entryService.removeEntryFromAccount(entry)),
        toArray()
      )
      : of([]);

    return removeBookingEntries$.pipe(
      switchMap(() => {
        const updatedAccount = this.accountStoreService.getSelectedAccount();
        const currentAccount = updatedAccount || account;

        const entriesToPay = (currentAccount.entries || [])
          .filter((entry: Entry) => entry.type !== ENTRY_TYPE.booking)
          .filter((entry: Entry) => !entry.isPending && !entry.isCharged)
          .filter((entry: Entry) => entry.quantityToDisplay > 0)
          .map((entry: Entry) => {
            const totalPaidQuantity =
              this.entryService.getTotalQuantityInPayments(entry);
            const quantityToPay = entry.quantityToDisplay - totalPaidQuantity;

            if (quantityToPay <= 0) {
              return null;
            }

            const entryToPay = EntryMapper.deepClone(entry);

            entryToPay.changeEntryQuantity(quantityToPay);
            entryToPay.updateQuantityToDisplay(quantityToPay);
            entryToPay.recalculateTotal();

            return entryToPay;
          })
          .filter((entry): entry is Entry => entry !== null);

        if (booking.financial.coverCharge.total +
          booking.financial.pendingConsumption === 0) {
          return of(entriesToPay);
        }

        return of(entriesToPay);
      })
    );
  }

  private processPayment(
    paymentMethod: PAYMENT_METHOD,
    totalAmountToPay: number,
    entriesToPay: Entry[]
  ): Observable<IPaymentResult> {
    return of({
      paymentMethod,
      changeDelivered: 0,
      totalAmount: totalAmountToPay,
      paymentType: PAYMENT_TYPE.full,
      totalDelivered: totalAmountToPay,
    }).pipe(
      switchMap((paymentSummary: IPaymentSummary) => {
        if (paymentMethod === PAYMENT_METHOD.cash) {
          return this.handlePayAmountWithCash(paymentSummary, entriesToPay);
        }

        if (paymentMethod === PAYMENT_METHOD.consumption) {
          return this.handlePayAmountWithConsumption(
            paymentSummary,
            entriesToPay
          );
        }

        if (paymentMethod === PAYMENT_METHOD.complement) {
          return this.handlePayAmountWithComplement(
            paymentSummary,
            entriesToPay
          );
        }

        return this.handlePayAmountWithCard(paymentSummary, entriesToPay);
      })
    );
  }

  private handlePayAmountWithCash(
    paymentSummary: IPaymentSummary,
    entriesToPay: Entry[]
  ): Observable<IPaymentResult> {
    return this.cashPrompt.askAmount(paymentSummary.totalAmount).pipe(
      switchMap((amountDelivered) => {
        paymentSummary.totalDelivered = amountDelivered;
        paymentSummary.changeDelivered = Math.max(0, amountDelivered - paymentSummary.totalAmount);

        return this.processCashPayment(paymentSummary, entriesToPay);
      })
    );
  }

  private handlePayAmountWithConsumption(
    paymentSummary: IPaymentSummary,
    entriesToPay: Entry[]
  ): Observable<IApiPaymentTransactionResponse> {
    const entriesWithConsumption = entriesToPay.map((entry: Entry) => {
      if (entry.consumption) {
        return entry;
      }

      entry.updateConsumption({type: CONSUMPTION_TYPE.consumption});

      return entry;
    });

    paymentSummary.totalAmount = entriesWithConsumption.reduce(
      (acc, entry) => acc + entry.total,
      0
    );

    const paymentDetails: IPaymentDetails = {
      paymentSummary,
      isAccountClosed: false,
      isTicketGenerated: false,
      entriesToPay: entriesWithConsumption,
      entriesRenderedAfterPayment: entriesWithConsumption,
    };

    return this.paymentService.payAccountAmount(paymentDetails).pipe(
      tap((paymentResponse: IApiPaymentTransactionResponse) => {
        if (paymentResponse.ticket && paymentDetails.isAccountClosed) {
          this.eventBus.publish(
            new AccountClosedEvent({
              accountId: this.selectedAccount?.id ?? '',
              trackId: this.selectedAccount?.trackId ?? '',
            })
          );

          return;
        }
      }),
      takeUntilDestroyed(this.destroyRef)
    );
  }

  private handlePayAmountWithComplement(
    paymentSummary: IPaymentSummary,
    entriesToPay: Entry[]
  ): Observable<IApiPaymentTransactionResponse> {
    const entriesWithConsumption = entriesToPay.map((entry: Entry) => {
      if (entry.consumption || entry.type === ENTRY_TYPE.consumption) {
        return entry;
      }

      entry.updateType(ENTRY_TYPE.consumption);

      return entry;
    });

    paymentSummary.totalAmount = entriesWithConsumption.reduce(
      (acc, entry) => acc + entry.total,
      0
    );

    const paymentDetails: IPaymentDetails = {
      paymentSummary,
      isAccountClosed: false,
      isTicketGenerated: false,
      entriesToPay: entriesWithConsumption,
      entriesRenderedAfterPayment: entriesWithConsumption,
    };

    return this.paymentService.payAccountAmount(paymentDetails).pipe(
      tap((paymentResponse: IApiPaymentTransactionResponse) => {
        if (paymentResponse.ticket && paymentDetails.isAccountClosed) {
          this.eventBus.publish(
            new AccountClosedEvent({
              accountId: this.selectedAccount?.id ?? '',
              trackId: this.selectedAccount?.trackId ?? '',
            })
          );

          return;
        }
      }),
      takeUntilDestroyed(this.destroyRef)
    );
  }

  private handlePayAmountWithCard(
    paymentSummary: IPaymentSummary,
    entriesToPay: Entry[]
  ): Observable<IApiPaymentTransactionResponse> {
    const paymentDetails: IPaymentDetails = {
      paymentSummary,
      isAccountClosed: false,
      isTicketGenerated: false,
      entriesToPay,
      entriesRenderedAfterPayment: entriesToPay,
    };

    return this.paymentService.payAccountAmount(paymentDetails).pipe(
      tap((paymentResponse: IApiPaymentTransactionResponse) => {
        if (paymentResponse.ticket && paymentDetails.isAccountClosed) {
          this.eventBus.publish(
            new AccountClosedEvent({
              accountId: this.selectedAccount?.id ?? '',
              trackId: this.selectedAccount?.trackId ?? '',
            })
          );

          return;
        }
      }),
      takeUntilDestroyed(this.destroyRef)
    );
  }

  private processCashPayment(
    paymentSummary: IPaymentSummary,
    entriesToPay: Entry[]
  ): Observable<IPaymentResult> {
    const currentAccount = this.accountStoreService.getSelectedAccount();

    if (!currentAccount) {
      this.logger.error(new Error('Error fetching current account'));

      return throwError(() => new Error('There is no current account'));
    }

    paymentSummary.paymentType = PAYMENT_TYPE.full;

    const paymentDetails: IPaymentDetails = {
      paymentSummary: paymentSummary,
      isAccountClosed: false,
      isTicketGenerated: false,
      entriesToPay: entriesToPay,
      entriesRenderedAfterPayment: entriesToPay,
    };

    return forkJoin({
      paymentResponse: this.paymentService.payAccountAmount(paymentDetails),
      openCashDrawer: this.hardwareManagerService.openCashDrawer(),
    }).pipe(
      map(({paymentResponse}) => {
        if (!this.paymentSettings.isFastCashPaymentEnabled()) {
          this.paymentService.notifyOnCashPaymentDelivered(
            paymentSummary
          );
        }

        if (paymentDetails.isAccountClosed) {
          this.eventBus.publish(
            new AccountClosedEvent({
              accountId: currentAccount?.id,
              trackId: currentAccount.trackId ?? '',
            })
          );
        }

        return paymentResponse;
      })
    );
  }
}
